import { createContext, lazy, Suspense, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CircleHelp } from 'lucide-react';
import { IconButton } from '../components/ui/icon-button.tsx';

/**
 * Help framework (in-app-help spec). Each page names its help page id;
 * controls carry `data-help-id`; texts live under help.<page>.<control>.
 */

type HelpState = { page: string; setPage: (page: string) => void };
const HelpContext = createContext<HelpState>({ page: 'shell', setPage: () => {} });

export function HelpProvider({ children }: { children: ReactNode }) {
  const [page, setPage] = useState('shell');
  const value = useMemo(() => ({ page, setPage }), [page]);
  return <HelpContext.Provider value={value}>{children}</HelpContext.Provider>;
}

/** Declares which help page applies while this component is shown. */
export function useHelpPage(page: string): void {
  const { setPage } = useContext(HelpContext);
  useEffect(() => setPage(page), [page, setPage]);
}

/** The help entries of a page: the controls of the page itself, then the shell's. */
export function controlsOnScreen(root: ParentNode = document): string[] {
  const ids = [...root.querySelectorAll<HTMLElement>('[data-help-id]')].map((el) => el.dataset.helpId!);
  return [...new Set(ids)];
}

export function highlight(id: string | null) {
  for (const el of document.querySelectorAll('.help-highlight')) el.classList.remove('help-highlight');
  if (!id) return;
  for (const el of document.querySelectorAll(`[data-help-id="${CSS.escape(id)}"]`)) el.classList.add('help-highlight');
}

const HelpPanel = lazy(() => import('./HelpPanel.tsx'));

export function HelpButton({ className }: { className?: string }) {
  const { t, i18n } = useTranslation();
  const { page } = useContext(HelpContext);
  const [open, setOpen] = useState(false);
  const [ids, setIds] = useState<string[]>([]);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const toggle = useCallback(() => {
    if (!open) setIds(controlsOnScreen());
    else highlight(null);
    setOpen(!open);
  }, [open]);

  const entries = ids
    .map((id) => {
      const key = `help.${id}`;
      return i18n.exists(`${key}.title`) ? { id, title: t(`${key}.title`), description: t(`${key}.description`) } : null;
    })
    .filter((e): e is { id: string; title: string; description: string } => e !== null);

  return (
    <>
      <IconButton
        ref={buttonRef}
        label={t('shell.help')}
        icon={<CircleHelp />}
        onClick={toggle}
        className={className}
        aria-expanded={open}
        data-help-id="shell.help"
      />
      {open ? (
        <Suspense fallback={null}>
          <HelpPanel
            title={t(`help.${page}.title`, { defaultValue: t('help.shell.title') })}
            intro={t(`help.${page}.intro`, { defaultValue: '' }) || undefined}
            closeLabel={t('help.close')}
            listLabel={t('help.listLabel')}
            entries={entries}
            onClose={() => {
              highlight(null);
              setOpen(false);
              buttonRef.current?.focus();
            }}
            onHighlight={highlight}
          />
        </Suspense>
      ) : null}
    </>
  );
}
